import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { FeatureSetting } from '@app/models/feature-setting';
import { CacheService } from '@app/services/cache.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { Environment } from '@environments/interface.environment';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class FeatureSettingsService {

  constructor(
    private http: HttpClient,
    private cache: CacheService,
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    private httpHelper: HttpHelper,
    private featureSettingsStoreService: FeatureSettingsStoreService) { }

  public readonly getFeatureSettingsCacheKey = 'FeatureSettingsService-getFeatureSettings';
  public useCache = true;
  private getFeatureSettings$: Observable<Array<FeatureSetting>>;
  private getAzureADApplicationObjectId$: Observable<string>;
  private reload$ = new Subject<void>();

  public reload() {
    this.reload$.next();
    this.getFeatureSettings$ = null;
    this.getAzureADApplicationObjectId$ = null;

    if (this.useCache) {
      this.cache.removeFromPersistentCache(this.getFeatureSettingsCacheKey);
    }
  }

  getAzureADApplicationObjectId(): Observable<string> {
    if (!this.getAzureADApplicationObjectId$) {
      this.getAzureADApplicationObjectId$ = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/${this.environment.msalConfig.tenant}/servicePrincipals?$filter=appId eq '${this.environment.msalConfig.auth.clientId}'&$select=id`)
        .pipe(
          map(result => {
            return result?.value[0]?.id;
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAzureADApplicationObjectId', result: null, rethrow: true })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));
    }
    return this.getAzureADApplicationObjectId$;
  }

  /**
   * This will get automatically invoked on app init
   * */
  getFeatureSettings(): Observable<Array<FeatureSetting>> {
    if (this.getFeatureSettings$) {
      return this.getFeatureSettings$;
    } else {
      this.getFeatureSettings$ = this.http.get<Array<FeatureSetting>>(`${Constants.API_ENDPOINT}/featuresettings`)
        .pipe(
          map(response => {
            console.log(`fetched result getFeatureSettings: `, response);
            return response.map(p => new FeatureSetting(p));
          }),
          tap(data => {
            this.useCache && this.cache.set(this.getFeatureSettingsCacheKey, data);
            data.forEach(f => this.featureSettingsStoreService.updateState(f));
          }),
          catchError(this.httpHelper.handleError({ operation: 'getFeatureSettings', result: new Array<FeatureSetting>(), rethrow: true, cacheKey: this.getFeatureSettingsCacheKey })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<FeatureSetting>>(this.getFeatureSettingsCacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getFeatureSettings$);
        }
      }
      return this.getFeatureSettings$;
    }
  }

  updateFeatureSetting(id: string, featureSetting: FeatureSetting): Observable<FeatureSetting> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.put<FeatureSetting>(`${Constants.API_ENDPOINT}/featuresettings/${id}`, featureSetting, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`updated result: `);
          console.log(response);
          this.reload();
          return featureSetting;
        }),
        tap(setting => {
          this.featureSettingsStoreService.updateState(setting);
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateFeatureSetting', result: new FeatureSetting(), rethrow: true })));

    return fetchOperation;
  }

  deleteFeatureSetting(id: string): Observable<FeatureSetting> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.delete<FeatureSetting>(`${Constants.API_ENDPOINT}/featuresettings/${id}`, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`deleted result: `);
          console.log(response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'deleteFeatureSetting', result: new FeatureSetting(), rethrow: true })));

    return fetchOperation;
  }

  addOrUpdateFeatureSetting(featureSetting: FeatureSetting): Observable<FeatureSetting> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.post<FeatureSetting>(`${Constants.API_ENDPOINT}/featuresettings`, featureSetting, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`added result: `);
          console.log(response);
          this.reload();
          if (response) {
            return new FeatureSetting(response);
          } else {
            return featureSetting;
          }
        }),
        tap(setting => {
          this.featureSettingsStoreService.updateState(setting);
        }),
        catchError(this.httpHelper.handleError({ operation: 'addFeatureSetting', result: new FeatureSetting(), rethrow: true })));

    return fetchOperation;
  }
}
